import React, { useState, useEffect, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import axiosInstance from "../../../services/AxiosInstance";
import swal from "sweetalert";
import { format, formatDistanceToNow } from "date-fns";
import PageTitle from "../../layouts/PageTitle";
import AutopilotWizard from "./AutopilotWizard";
import AutopilotReview from "./AutopilotReview";
import AutopilotActivity from "./AutopilotActivity";
import AutopilotSettings from "./AutopilotSettings";

const errMsg = (e) => e?.response?.data?.message || e.message || "Something went wrong";
const TYPE_LABEL = { rss: "RSS", web: "WEB", youtube: "YT", reddit: "REDDIT", telegram: "TG" };

const Stat = ({ label, value, sub }) => (
  <div className="col-6 col-md-3 mb-3">
    <div className="card mb-0 h-100"><div className="card-body text-center py-3">
      <h3 className="mb-0" style={{ color: "#17556D" }}>{value}</h3>
      <div className="text-muted small">{label}</div>
      {sub && <div className="text-muted" style={{ fontSize: 11 }}>{sub}</div>}
    </div></div>
  </div>
);

const health = (s) => {
  if (s.status === "paused") return ["secondary", "Paused"];
  if (s.lastError) return ["warning", "Needs attention"];
  return ["success", "Healthy"];
};

const ChannelAutopilot = () => {
  const [tab, setTab] = useState("dashboard");
  const [data, setData] = useState(null);
  const [blocked, setBlocked] = useState("");
  const [loading, setLoading] = useState(true);
  const [wizard, setWizard] = useState(false);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await axiosInstance.get("autopilot/overview");
      setData(res.data); setBlocked("");
    } catch (e) {
      if (e?.response?.status === 403) setBlocked(errMsg(e)); else swal("Error", errMsg(e), "error");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const patch = async (s, body, okMsg) => {
    setBusy(s._id);
    try { await axiosInstance.put(`autopilot/sources/${s._id}`, body); if (okMsg) swal("Done", okMsg, "success"); await load(); }
    catch (e) { swal("Error", errMsg(e), "error"); }
    setBusy("");
  };
  const runNow = async (s) => {
    setBusy(s._id);
    try { const { data: d } = await axiosInstance.post(`autopilot/sources/${s._id}/run`); swal("Checked", d.message, "success"); await load(); }
    catch (e) { swal("Error", errMsg(e), "error"); }
    setBusy("");
  };
  const remove = async (s) => {
    const ok = await swal({ title: `Remove "${s.name}"?`, text: "Its scheduled posts will be cancelled.", icon: "warning", buttons: true, dangerMode: true });
    if (!ok) return;
    try { await axiosInstance.delete(`autopilot/sources/${s._id}`); await load(); }
    catch (e) { swal("Error", errMsg(e), "error"); }
  };

  if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>;

  if (blocked) return (
    <>
      <PageTitle activeMenu="Autopilot" motherMenu="Channels" />
      <div className="card text-center p-5">
        <h4>Autopilot is not available</h4>
        <p className="text-muted mt-2">{blocked}</p>
        <Link to="/packs" className="btn btn-primary mt-3" style={{ maxWidth: 200, margin: "0 auto" }}>Upgrade Plan</Link>
      </div>
    </>
  );

  const st = data?.stats || {};
  const sources = data?.sources || [];

  return (
    <>
      <style>{".gap-2{gap:.5rem}.gap-3{gap:1rem}"}</style>
      <PageTitle activeMenu="Autopilot" motherMenu="Channels" />

      <ul className="nav nav-pills mb-3">
        {[["dashboard", "Dashboard"], ["review", "Review Inbox"], ["activity", "Activity"], ["settings", "AI & Style"]].map(([k, l]) => (
          <li className="nav-item" key={k}>
            <button className={`nav-link ${tab === k ? "active" : ""}`} onClick={() => setTab(k)}>
              {l}{k === "review" && st.waitingReview > 0 && <span className="badge badge-danger ml-2">{st.waitingReview}</span>}
            </button>
          </li>
        ))}
      </ul>

      {tab === "review" && <AutopilotReview onChanged={load} />}
      {tab === "activity" && <AutopilotActivity />}
      {tab === "settings" && <AutopilotSettings onChanged={load} />}

      {tab === "dashboard" && (
        <>
          {!st.hasAIKey && (
            <div className="alert alert-warning d-flex justify-content-between align-items-center flex-wrap gap-2">
              <span><strong>Step 1:</strong> add your AI key so Autopilot can write posts for you.</span>
              <button className="btn btn-sm btn-primary" onClick={() => setTab("settings")}>Add AI key</button>
            </div>
          )}

          <div className="row">
            <Stat label="Posted today" value={st.postedToday ?? 0} />
            <Stat label="Waiting for review" value={st.waitingReview ?? 0} />
            <Stat label="Next post" value={st.nextPostAt ? format(new Date(st.nextPostAt), "HH:mm") : "-"} sub={st.nextPostAt ? format(new Date(st.nextPostAt), "dd MMM") : ""} />
            <Stat label="AI drafts this month" value={st.aiUsed ?? 0} />
          </div>

          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">My sources</h5>
            <button className="btn btn-primary btn-sm" onClick={() => setWizard(true)}>+ Add source</button>
          </div>

          {sources.length === 0 ? (
            <div className="card text-center p-5">
              <h4>Put your channel on autopilot</h4>
              <p className="text-muted">Connect a website, blog, YouTube, Reddit or Telegram channel. The AI rewrites new items and posts them for you on a schedule.</p>
              <button className="btn btn-primary mt-2" style={{ maxWidth: 220, margin: "0 auto" }} onClick={() => setWizard(true)}>Set up Autopilot</button>
            </div>
          ) : sources.map((s) => {
            const [hbg, hl] = health(s);
            return (
              <div className="card mb-3" key={s._id}>
                <div className="card-body">
                  <div className="d-flex justify-content-between flex-wrap gap-2">
                    <div>
                      <span className="badge badge-dark mr-2">{TYPE_LABEL[s.type] || s.type}</span>
                      <strong>{s.name}</strong>
                      <span className="text-muted ml-2">→ 📢 {s.channelName}</span>
                      <div className="small text-muted mt-1">
                        {s.postsPerDay} posts/day · {String(s.windowStart).padStart(2, "0")}:00–{String(s.windowEnd % 24).padStart(2, "0")}:00 ·{" "}
                        {s.lastCheckedAt ? `checked ${formatDistanceToNow(new Date(s.lastCheckedAt))} ago` : "not checked yet"}
                      </div>
                      <div className="small text-muted">
                        {s.totals?.ingested || 0} found · {s.totals?.posted || 0} posted · {s.totals?.rejected || 0} rejected · {s.totals?.failed || 0} failed
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`badge badge-${hbg} mb-2`}>{hl}</span>
                      <div className="btn-group btn-group-sm d-block">
                        <button className={`btn ${s.mode === "review" ? "btn-primary" : "btn-outline-secondary"}`} disabled={busy === s._id} onClick={() => s.mode !== "review" && patch(s, { mode: "review" })}>Review</button>
                        <button className={`btn ${s.mode === "auto" ? "btn-primary" : "btn-outline-secondary"}`} disabled={busy === s._id} onClick={() => s.mode !== "auto" && patch(s, { mode: "auto" })}>Full Auto</button>
                      </div>
                    </div>
                  </div>

                  {(s.lastError || s.pauseReason) && (
                    <div className="alert alert-warning py-2 small mt-3 mb-0">{s.status === "paused" ? s.pauseReason : s.lastError}</div>
                  )}
                  {s.suggestAuto && (
                    <div className="alert alert-success py-2 small mt-3 mb-0 d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <span>This source is doing great ({s.approvedClean} clean approvals). Switch to Full Auto?</span>
                      <button className="btn btn-sm btn-success" onClick={() => patch(s, { mode: "auto" }, "Now in Full Auto mode.")}>Switch to Full Auto</button>
                    </div>
                  )}

                  <div className="d-flex gap-2 mt-3 flex-wrap">
                    {s.status === "paused"
                      ? <button className="btn btn-outline-primary btn-sm" disabled={busy === s._id} onClick={() => patch(s, { status: "active" })}>Resume</button>
                      : <button className="btn btn-outline-secondary btn-sm" disabled={busy === s._id} onClick={() => patch(s, { status: "paused" })}>Pause</button>}
                    <button className="btn btn-outline-secondary btn-sm" disabled={busy === s._id} onClick={() => runNow(s)}>
                      {busy === s._id ? <Spinner animation="border" size="sm" /> : "Check now"}
                    </button>
                    <button className="btn btn-outline-danger btn-sm" onClick={() => remove(s)}>Delete</button>
                  </div>
                </div>
              </div>
            );
          })}

          {data?.upcoming?.length > 0 && (
            <div className="card">
              <div className="card-header"><h5 className="card-title mb-0">Up next</h5></div>
              <div className="card-body">
                {data.upcoming.map((u) => (
                  <div key={u._id} className="d-flex gap-3 mb-2">
                    <div className="text-nowrap" style={{ width: 96, color: "#17556D" }}><strong>{format(new Date(u.scheduledAt), "dd MMM HH:mm")}</strong></div>
                    <div className="small"><span className="text-muted">{u.channelName}</span> {u.text}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <AutopilotWizard show={wizard} onClose={() => setWizard(false)} onCreated={load} />
    </>
  );
};

export default ChannelAutopilot;
